import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Element, scroller } from 'react-scroll';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import compact from 'lodash/compact';
import Accordion from 'brastrap/common/accordion/Accordion';
import SectionNav from 'brastrap/common/section-nav/SectionNav';
import GoogleMap from 'brastrap/editorial/map/Map.jsx';

const SHOPS_SCROLL_POINT = 'scrollPoint.ShopsSection';

const shopCompare = (shopA, shopB) => shopA.name.localeCompare(shopB.name);

/**
 * If the currentCode is in the array then move it to the first position
 *
 * @param {Array} array
 * @param {String} currentCode
 * @return {Array}
 */
const sortCountryCodes = (array, currentCode) =>
  array.sort((codeA, codeB) => {
    if (codeA === currentCode) return -1;
    if (codeB === currentCode) return 1;
    return 0;
  });

const generateAddressString = fields =>
  fields.filter(field => !!field).join(', ');

const addressLines = addr =>
  compact([
    addr.address2,
    addr.address3,
    addr.town,
    addr.region,
    addr.postCode,
  ]);

const toNavItem = shop => {
  const {
    slug,
    name,
    address: { address2, address3, town, region, postCode },
  } = { address: {}, ...shop };
  return {
    url: `/shops/${slug}/`,
    label: name,
    note:
      shop.country === 'US'
        ? generateAddressString([address2, address3, town, region, postCode])
        : generateAddressString([address2]),
  };
};

const hasMarkerAndAddress = shop =>
  shop.location &&
  shop.location.lat &&
  shop.location.lon &&
  Object.keys(shop.address).length > 0;

const formatShopForMarker = shop => ({
  location: shop.location,
  address: addressLines(shop.address),
  url: `/shops/${shop.slug}/`,
  label: shop.name,
});

const messages = defineMessages({
  title: { id: 'shops-section.shop-list', defaultMessage: 'List of shops ' },
});

const shopLabels = defineMessages({
  GB: { id: 'shops-section.shop-labels.gb', defaultMessage: 'UK Shops' },
  US: { id: 'shops-section.shop-labels.us', defaultMessage: 'USA Shops' },
});

class ShopsSection extends Component {
  state = {
    countryCode: this.props.selectedLocale.slice(-2),
  };

  componentWillMount = () => {
    this.props.updateAccordion(this.state.countryCode);
  };

  /**
   * @return {*}
   */
  render() {
    const {
      intl: { formatMessage },
      accordion,
      googleApiKey,
      shops,
      updateAccordion,
    } = this.props;

    const countriesWithShops = shops.reduce((countries, shop) => {
      if (!countries.includes(shop.country)) {
        countries.push(shop.country);
      }

      return countries;
    }, []);

    const mapHeight = 720;

    return (
      <div className="c-shops-section">
        <Element name={SHOPS_SCROLL_POINT} />
        <div className="l-grid">
          <div className="l-grid__unit l-grid__unit--nav">
            {countriesWithShops.includes('US') ? (
              /* TODO: Strip this conditional and use the accordion exclusively once the first physical US store opens */
              <Accordion activeChild={accordion}>
                {sortCountryCodes(
                  countriesWithShops,
                  this.state.countryCode
                ).map(country => (
                  <Accordion.Item
                    id={country}
                    key={country}
                    label={
                      shopLabels[country]
                        ? formatMessage(shopLabels[country])
                        : `${country} Shops`
                    }
                    onClick={() => {
                      updateAccordion(country !== accordion ? country : null);
                      scroller.scrollTo(SHOPS_SCROLL_POINT);
                    }}
                  >
                    <SectionNav
                      id="shops-section__nav"
                      title={formatMessage(messages.title)}
                      items={shops
                        .filter(shop => shop.country === country)
                        .sort(shopCompare)
                        .map(toNavItem)}
                    />
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <SectionNav
                id="shops-section__nav"
                title={formatMessage(messages.title)}
                items={shops.sort(shopCompare).map(toNavItem)}
              />
            )}
          </div>
          <div className="l-grid__unit l-grid__unit--content">
            <div className="s-prose">
              <GoogleMap
                width="100%"
                height={mapHeight}
                shops={(countriesWithShops.includes(
                  'US'
                ) /* Remove conditional when US shop opens */
                  ? shops.filter(
                      shop =>
                        shop.country === (accordion || this.state.countryCode)
                    )
                  : shops
                )
                  .filter(hasMarkerAndAddress)
                  .map(formatShopForMarker)}
                showAllMarkup
                googleApiKey={googleApiKey}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ShopsSection.propTypes = {
  accordion: PropTypes.string,
  selectedLocale: PropTypes.string,
  shops: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        address3: PropTypes.string,
        region: PropTypes.string,
        town: PropTypes.string,
        postCode: PropTypes.string,
      }),
      location: PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    })
  ),
  googleApiKey: PropTypes.string,
  intl: intlShape,
  updateAccordion: PropTypes.func,
};

ShopsSection.defaultProps = {
  shops: [],
  intl: intlShape,
  updateAccordion: () => {},
};

export default injectIntl(ShopsSection);
